import { axiosInstance } from '../utils/axios';

export interface AdminOtpRequest {
  usernameOrEmail: string;
}

export interface AdminLoginRequest {
  usernameOrEmail: string;
  otp: string;
}

export interface AdminLoginResponse {
  success: boolean;
  message: string;
  data: {
    token: string;
    admin: {
      id: string;
      username: string;
      email: string;
      fullName: string;
      lastLoginAt?: string;
    };
  };
}

export interface AdminInfo {
  id: string;
  username: string;
  email: string;
  fullName: string;
  lastLoginAt?: string;
  createdAt: string;
}

// Admin Authentication
export const requestAdminOtp = async (request: AdminOtpRequest) => {
  const response = await axiosInstance.post('/admin/auth/request-otp', request);
  return response.data;
};

export const adminLogin = async (credentials: AdminLoginRequest): Promise<AdminLoginResponse> => {
  const response = await axiosInstance.post('/admin/auth/login', credentials);
  return response.data;
};

export const adminLogout = async () => {
  const response = await axiosInstance.post('/admin/auth/logout');
  return response.data;
};

export const getAdminInfo = async (): Promise<AdminInfo> => {
  const response = await axiosInstance.get('/admin/auth/me');
  return response.data.data;
};

export const changeAdminPassword = async (passwords: { currentPassword: string; newPassword: string }) => {
  const response = await axiosInstance.post('/admin/auth/change-password', passwords);
  return response.data;
};

// Admin Rewards Management
export const getAdminRewards = async (page: number = 1, limit: number = 20, category?: string, isActive?: boolean, ownerId?: string) => {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString()
  });
  
  if (category) params.append('category', category);
  if (isActive !== undefined) params.append('isActive', isActive.toString());
  if (ownerId) params.append('ownerId', ownerId);
  
  const response = await axiosInstance.get(`/admin/api/rewards?${params.toString()}`);
  return response.data;
};

export const createAdminReward = async (rewardData: any) => {
  const response = await axiosInstance.post('/admin/api/rewards', rewardData);
  return response.data;
};

export const updateAdminReward = async (rewardId: string, rewardData: any) => {
  const response = await axiosInstance.put(`/admin/api/rewards/${rewardId}`, rewardData);
  return response.data;
};

export const deleteAdminReward = async (rewardId: string) => {
  const response = await axiosInstance.delete(`/admin/api/rewards/${rewardId}`);
  return response.data;
};

export const uploadAdminImage = async (file: FormData) => {
  // Backend serves uploads at /api/upload (authenticated)
  const response = await axiosInstance.post('/api/upload', file);
  return response.data;
};

export const getAdminRewardById = async (rewardId: string) => {
  const response = await axiosInstance.get(`/admin/api/rewards/${rewardId}`);
  return response.data;
};

export const getAdminRewardStats = async () => {
  const response = await axiosInstance.get('/admin/api/rewards/stats');
  return response.data;
};

export const getAdminRewardCategories = async (): Promise<string[]> => {
  const response = await axiosInstance.get('/admin/api/rewards/categories');
  return response.data.data;
};

// Admin Redeem Requests Management
export const getAdminRedeemRequests = async (page: number = 1, limit: number = 20, status?: string, rewardId?: string, ownerId?: string) => {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString()
  });
  
  if (status) params.append('status', status);
  if (rewardId) params.append('rewardId', rewardId);
  if (ownerId) params.append('ownerId', ownerId);
  
  const response = await axiosInstance.get(`/admin/api/rewards/redeem-requests?${params.toString()}`);
  return response.data;
};

export const acceptAdminRedemptionRequest = async (requestId: string) => {
  const response = await axiosInstance.post(`/admin/api/rewards/redeem-requests/${requestId}/accept`);
  return response.data;
};

export const rejectAdminRedemptionRequest = async (requestId: string, rejectionReason?: string) => {
  const response = await axiosInstance.post(`/admin/api/rewards/redeem-requests/${requestId}/reject`, {
    rejectionReason: rejectionReason || ''
  });
  return response.data;
};

export const markAdminAsDelivered = async (requestIds: string[]) => {
  const response = await axiosInstance.post('/admin/api/rewards/redeem-requests/deliver', {
    requestIds
  });
  return response.data;
};

// Get club owners for reward assignment
export const getClubOwners = async () => {
  const response = await axiosInstance.get('/admin/api/club-owners');
  return response.data;
};

// Admin Points Management
export interface Customer {
  id: string;
  email: string;
  fullName?: string;
  currentPoints: number;
  createdAt: string;
}

export interface CustomersResponse {
  success: boolean;
  data: {
    customers: Customer[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  };
}

export interface AddPointsRequest {
  customerIds: string[];
  points: number;
  description: string;
}

export interface AddPointsResponse {
  success: boolean;
  message: string;
  data: {
    results: Array<{
      customerId: string;
      email: string;
      success: boolean;
      error?: string;
    }>;
    summary: {
      total: number;
      successful: number;
      failed: number;
    };
  };
}

export const getCustomers = async (page = 1, limit = 50, search = ''): Promise<CustomersResponse> => {
  const response = await axiosInstance.get('/admin/api/customers', {
    params: { page, limit, search }
  });
  return response.data;
};

export const addPoints = async (data: AddPointsRequest): Promise<AddPointsResponse> => {
  const response = await axiosInstance.post('/admin/api/add-points', data);
  return response.data;
};